import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {objectFromEntries} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import ContainerContentConcentrationForm from 'samples/ContainerContentConcentrationForm';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import * as TransferActions from 'samples/transfers/TransferActions';
import {OnCompleteCalculateConcentrationsCallback} from 'samples/transfers/TransferActions';
import {ContainerTransferParam} from 'samples/transfers/TransferDAO';
import {ConfirmConcentrationsData} from 'samples/transfers/TransferModalUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {Title} from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {SelectChangeEvent} from 'util/Types';

import {
  ContainerBioentitiesQuery,
  ContainerBioentitiesQueryVariables,
} from './__generated/ContainerBioentitiesQuery';

type ContainerBioentitiesQueryBioentitiesBasicFolderItemNode = ExtractByTypename<
  ContainerBioentitiesQuery['bioentities'][number],
  'BasicFolderItemNode'
>;

export const CONTAINED_BIOENTITIES_QUERY: DocumentNode = gql`
  query ContainerBioentitiesQuery($bioentityIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function _getShouldCalculateConcentrations(
  shouldCalculateConcentrations: boolean,
  areSolidUnitsInvolved: boolean
): boolean {
  // We do not support concentration calculations for solid transfers at this time
  return areSolidUnitsInvolved ? false : shouldCalculateConcentrations;
}

export interface BaseConfirmConcentrationsPaneState {
  confirmConcentrationsData: ConfirmConcentrationsData | null;
  shouldCalculateConcentrations: boolean;
  areSolidUnitsInvolved: boolean;
}

export const DEFAULT_BASE_CONFIRM_CONCENTRATIONS_STATE: BaseConfirmConcentrationsPaneState = {
  confirmConcentrationsData: null,
  shouldCalculateConcentrations: true,
  areSolidUnitsInvolved: false,
};

export interface ConfirmConcentrationsPaneState extends BaseConfirmConcentrationsPaneState {
  errorMessage: string | null;
}

interface ConfirmConcentrationsPaneProps {
  paneState: ConfirmConcentrationsPaneState;
  onChangeConcentration: (
    containerId: string,
    containedItemId: string,
    concentration: Partial<LegacyMeasurement>
  ) => void;
  allowRecalculateConcentrations: boolean;
  // Must be provided if allowRecalculateConcentrations is true
  onChangeShouldCalculate?: (shouldCalcConcentrations: boolean) => void;
  // Must be provided if allowRecalculateConcentrations is true
  getConfirmConcentrationArgsAndCallbacks?: () => {
    containerTransfers: Array<ContainerTransferParam>;
    inputContainableConcentration: LegacyMeasurement | null;
    onStart?: () => void;
    onComplete?: OnCompleteCalculateConcentrationsCallback;
    onFailure?: (errorMessage: string) => void;
  };
}

interface ConnectedProps {
  bioentitySummariesById: {[id: string]: BioentitySummary};
}

export class ConfirmConcentrationsPane extends React.Component<
  ConfirmConcentrationsPaneProps & ConnectedProps
> {
  _handleChangeShouldCalculate = (event: SelectChangeEvent): void => {
    const {value} = event.target;
    assert(['true', 'false'].includes(value), 'Expected true or false');
    const booleanValue = value === 'true';
    assert(this.props.onChangeShouldCalculate != null, 'Expected handler for should calc concentrations');
    this.props.onChangeShouldCalculate(booleanValue);
  };

  _handleRecalculate = (): void => {
    assert(
      this.props.getConfirmConcentrationArgsAndCallbacks != null,
      'Expected function to get confirm concentration args'
    );
    const {shouldCalculateConcentrations, areSolidUnitsInvolved} = this.props.paneState;
    TransferActions.calculateConcentrations({
      ...this.props.getConfirmConcentrationArgsAndCallbacks(),
      shouldCalculateConcentrations: _getShouldCalculateConcentrations(
        shouldCalculateConcentrations,
        areSolidUnitsInvolved
      ),
    });
  };

  _handleRecalculateKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>): void => {
    if (event.repeat) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this._handleRecalculate();
    }
  };

  _getContainedItem(containedItemId: string): BioentitySummary {
    const {bioentitySummariesById} = this.props;
    return bioentitySummariesById[containedItemId];
  }

  override render(): JSX.Element {
    const {confirmConcentrationsData, errorMessage, areSolidUnitsInvolved, shouldCalculateConcentrations} =
      this.props.paneState;
    return (
      <div {...getTestProps({component: 'ConfirmConcentrationsPane', element: 'root'})}>
        {(() => {
          if (errorMessage) {
            return (
              <div {...getTestProps({component: 'ConfirmConcentrationsPane', element: 'errors'})}>
                <Title>Errors:</Title>
                <div className="u-danger-color">{errorMessage}</div>
              </div>
            );
          } else if (!confirmConcentrationsData) {
            return (
              <div className="u-justify-content-center">
                <HorizontalThrobber />
              </div>
            );
          } else if (!confirmConcentrationsData.length) {
            return 'No transfers will be performed.';
          } else {
            return (
              <div
                {...getTestProps({
                  component: 'ConfirmConcentrationsPane',
                  element: 'finalConcentrationsTable',
                })}
              >
                <Title>Final Concentrations</Title>
                <Row className="transferModal-tableHeader">
                  <Col xs={2}>Destination</Col>
                  <Col xs={5}>Name</Col>
                  <Col xs={5}>Concentration</Col>
                </Row>
                <div className="transferModal-tableRows">
                  {confirmConcentrationsData.map((containerConcentrationsData, i) => (
                    <Row className="transferModal-tableRow" key={i}>
                      <Col xs={2}>{containerConcentrationsData.label}</Col>
                      <Col xs={10}>
                        {containerConcentrationsData.containerContents.map((content) => (
                          <div
                            key={`${content.containerId}/${content.containedItemId}`}
                            {...getTestProps({
                              component: 'ConfirmConcentrationsPane',
                              element: 'concentrationRow',
                              key: `${content.containerId}-${content.containedItemId}`,
                            })}
                          >
                            <ContainerContentConcentrationForm
                              containedItemId={content.containedItemId}
                              containedItemName={this._getContainedItem(content.containedItemId).name}
                              concentration={content.concentration}
                              containerId={content.containerId}
                              onChange={this.props.onChangeConcentration}
                            />
                          </div>
                        ))}
                      </Col>
                    </Row>
                  ))}
                </div>
                {this.props.allowRecalculateConcentrations && (
                  <div
                    {...getTestProps({
                      component: 'ConfirmConcentrationsPane',
                      element: 'recalculateConcentrations',
                    })}
                  >
                    <Title>Recalculate Concentrations</Title>
                    <div className="u-flex-display">
                      <select
                        onChange={this._handleChangeShouldCalculate}
                        value={_getShouldCalculateConcentrations(
                          shouldCalculateConcentrations,
                          areSolidUnitsInvolved
                        ).toString()}
                      >
                        {!areSolidUnitsInvolved && (
                          <option value="true">
                            Calculate based on total {getQuantityDisplayString(false, false)}
                          </option>
                        )}
                        <option value="false">Do not change original concentrations</option>
                      </select>
                      <span
                        aria-label="Recalculate concentrations"
                        className="u-small-spacing-left u-tiny-spacing-top u-pointer"
                        onClick={this._handleRecalculate}
                        onKeyDown={this._handleRecalculateKeyDown}
                        role="button"
                        tabIndex={0}
                      >
                        <BenchlingIcon icon="refresh" />
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          }
        })()}
      </div>
    );
  }
}

export default function QueryingConfirmConcentrationsPane(
  props: ConfirmConcentrationsPaneProps
): JSX.Element {
  let bioentityIds: Array<string> = [];
  if (props.paneState.confirmConcentrationsData) {
    // eslint-disable-next-line no-restricted-syntax
    const idsToFetch = _.uniq(
      props.paneState.confirmConcentrationsData
        .map((containerConcentrationsData) =>
          containerConcentrationsData.containerContents.map(({containedItemId}) => containedItemId)
        )
        .flat()
    );
    bioentityIds = idsToFetch.filter((id) => APIUtil.isBiologicalEntity(id));
  }

  return (
    <BenchlingQuery<ContainerBioentitiesQuery, ContainerBioentitiesQueryVariables>
      query={CONTAINED_BIOENTITIES_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{bioentityIds}}
    >
      {(data) => (
        <ConfirmConcentrationsPane
          {...props}
          bioentitySummariesById={objectFromEntries(
            data.bioentities.map((bioentity: ContainerBioentitiesQueryBioentitiesBasicFolderItemNode) => [
              bioentity.id,
              graphQLToREST('BioentitySummary', bioentity),
            ])
          )}
        />
      )}
    </BenchlingQuery>
  );
}
